<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import moment from "moment";

import { useCaseStore } from "@/stores/case-store";
import { useBrandStore } from "@/stores/brand-store";
import { getGracePeriod } from "@/services/UtilitiesService";

import ConfirmPopover from "@/components/ConfirmPopover.vue";

import type AgreementPeriod from "@/models/AgreementPeriod";
import type { DockviewPanelComponentProps, DockviewPanelComponentEmits } from "@/hooks/use-case-panel";

const FORMAT = "YYYY MM DD";

defineProps<DockviewPanelComponentProps<false>>();
defineEmits<DockviewPanelComponentEmits>();

let selectedPeriod = _ref<AgreementPeriod | null>(null);
let editForm = _ref({
  from: "",
  to: "",
  graceTo: "",
  error: "",
});
let createForm = _ref({
  from: "",
  to: "",
  graceTo: "",
  error: "",
});
const graceToEOM = _computed(() => {
  const viewerConfig = useBrandStore().viewerConfig;
  return !!viewerConfig.timeline?.upper?.periodOverlapMonthsToEOM;
});

const agreementPeriods = _computed(() => {
  return useCaseStore()
    .config.agreementPeriods.filter((ap) => ap.from && ap.to)
    .map(getGracePeriod)
    .sort((a, b) => {
      const aInstance = moment(a.from, FORMAT, true);
      const bInstance = moment(b.from, FORMAT, true);

      if (aInstance.isAfter(bInstance)) {
        return 1;
      }
      return -1;
    });
});

const defaultGraceToMonths = _computed(() => {
  return useBrandStore().viewerConfig.timeline?.upper?.periodOverlapMonths || 0;
});

const isGraceDisabled = _computed(() => {
  return !!useBrandStore().viewerConfig.timeline?.upper?.disableOverlapMonths;
});

function validateOverlappingPeriod(periodA: Pick<AgreementPeriod, "from" | "to">, ignoreId?: string) {
  const overlappingPeriod = agreementPeriods.value.find((periodB) => {
    if (ignoreId != null && periodB.id === ignoreId) return false;

    const periodAfrom = moment(periodA.from, FORMAT, true);
    const periodAto = moment(periodA.to, FORMAT, true);
    const periodBfrom = moment(periodB.from, FORMAT, true);
    const periodBto = moment(periodB.to, FORMAT, true);

    return (
      periodAfrom.isBetween(periodBfrom, periodBto) ||
      periodAto.isBetween(periodBfrom, periodBto) ||
      periodBfrom.isBetween(periodAfrom, periodAto) ||
      periodBto.isBetween(periodAfrom, periodAto) ||
      periodAfrom.isSame(periodBfrom) ||
      periodAfrom.isSame(periodBto) ||
      periodAto.isSame(periodBfrom) ||
      periodAto.isSame(periodBto)
    );
  });

  if (overlappingPeriod)
    return `Date range overlaps with ${overlappingPeriod.from} - ${overlappingPeriod.to}. Overlapping periods are not supported.`;

  return "";
}

function validateDate(from: string, to: string, graceTo: string): string {
  const fromInstance = moment(from, FORMAT, true);
  const toInstance = moment(to, FORMAT, true);
  const graceToInstance = moment(graceTo, FORMAT, true);

  if (
    fromInstance.isBefore(moment("1900 01 01", "YYYY MM DD", true)) ||
    toInstance.isBefore(moment("1900 01 01", "YYYY MM DD", true)) ||
    graceToInstance.isBefore(moment("1900 01 01", "YYYY MM DD", true))
  ) {
    return `Date cannot be earlier than ${moment("1900 01 01", "YYYY MM DD", true).format("YYYY MM DD")}`;
  }

  if (!fromInstance.isValid() || !toInstance.isValid() || (!isGraceDisabled.value && !graceToInstance.isValid())) {
    return `Date should be in 'YYYY MM DD' format`;
  }

  if (fromInstance.isAfter(toInstance)) {
    return `From date cannot be after to date`;
  }

  if (toInstance.isAfter(graceToInstance) && !isGraceDisabled.value) {
    return `To date cannot be after grace to date`;
  }

  return "";
}

function selectPeriod(period: AgreementPeriod) {
  editForm.value = {
    from: period.from,
    to: period.to,
    graceTo: period.graceTo || "",
    error: "",
  };
  selectedPeriod.value = period;
}
async function addPeriod() {
  createForm.value.error = "";

  const id = "_" + Math.random().toString(36).substr(2, 9);

  if (!createForm.value.graceTo && moment(createForm.value.to, FORMAT, true).isValid()) {
    const graceToMoment = moment(createForm.value.to, FORMAT, true).add(defaultGraceToMonths.value, "M");
    if (graceToEOM.value) graceToMoment.endOf("month");
    createForm.value.graceTo = graceToMoment.format(FORMAT);
  }

  const { from, to, graceTo } = createForm.value;
  const error = validateDate(from, to, graceTo) || validateOverlappingPeriod({ from, to });
  if (error) {
    createForm.value.error = error;
    return;
  }
  await useCaseStore().updateAgreementPeriod({ id, from, to, graceTo });
  createForm.value = { from: "", to: "", graceTo: "", error: "" };
}

async function savePeriod(): Promise<void> {
  createForm.value.error = "";

  if (!selectedPeriod.value) {
    return;
  }
  if (!editForm.value.graceTo && moment(editForm.value.to, FORMAT, true).isValid()) {
    editForm.value.graceTo = moment(editForm.value.to, FORMAT, true).add(defaultGraceToMonths.value, "M").format(FORMAT);
  }

  const { from, to, graceTo } = editForm.value;

  const error = validateDate(from, to, graceTo) || validateOverlappingPeriod({ from, to }, selectedPeriod.value.id);
  if (error) {
    editForm.value.error = error;
    return;
  }

  await useCaseStore().updateAgreementPeriod({
    id: selectedPeriod.value.id,
    from,
    to,
    graceTo,
  });
  selectedPeriod.value = null;
}

async function deletePeriod(period: AgreementPeriod) {
  await useCaseStore().deleteAgreementPeriod(period);
}
</script>
<template>
  <div class="tw-size-full tw-overflow-y-auto">
    <h2 class="tw-bg-gray-900 tw-p-2 tw-text-base tw-font-semibold tw-text-white">Manage Service Periods</h2>
    <div class="tw-mx-2 tw-mt-2 tw-flex tw-items-center">
      <input
        v-model="createForm.from"
        type="text"
        placeholder="From date"
        class="tw-my-1 tw-mr-2 tw-rounded-sm tw-border tw-p-1 tw-pr-4"
      />
      <input
        v-model="createForm.to"
        type="text"
        placeholder="To date"
        class="tw-my-1 tw-mr-2 tw-rounded tw-border tw-p-1 tw-pr-4"
      />
      <input
        v-if="!isGraceDisabled"
        v-model="createForm.graceTo"
        type="text"
        placeholder="Grace To date"
        class="tw-my-1 tw-rounded tw-border tw-p-1 tw-pr-4"
      />
      <button class="tw-btn-md-gray tw-b-r tw-ml-2" @click="addPeriod">Add</button>
    </div>
    <div v-if="createForm.error" class="tw-mx-2 tw-text-xs tw-text-red-700">
      {{ createForm.error }}
    </div>
    <ul class="tw-m-2">
      <li v-for="period in agreementPeriods" :key="period.id" class="tw-py-1">
        <template v-if="selectedPeriod && selectedPeriod.id === period.id">
          <div class="tw-flex tw-items-center">
            <input
              v-model="editForm.from"
              type="text"
              placeholder="From date"
              class="tw-my-1 tw-mr-2 tw-rounded-sm tw-border tw-p-1 tw-pr-4"
            />
            <input
              v-model="editForm.to"
              type="text"
              placeholder="To date"
              class="tw-my-1 tw-mr-2 tw-rounded tw-border tw-p-1 tw-pr-4"
            />
            <input
              v-if="!isGraceDisabled"
              v-model="editForm.graceTo"
              type="text"
              placeholder="Grace To date"
              class="tw-my-1 tw-rounded tw-border tw-p-1 tw-pr-4"
            />
            <button class="tw-btn-md-gray tw-b-r tw-ml-2" @click="savePeriod">Save</button>
            <button class="tw-btn-md-gray tw-b-r tw-ml-2" @click="selectedPeriod = null">Cancel</button>
          </div>
          <div v-if="editForm.error" class="tw-text-xs tw-text-red-700">
            {{ editForm.error }}
          </div>
        </template>
        <div v-else>
          <span class="tw-mr-2">{{ period.from }} - {{ period.to }}</span>
          <span v-if="!isGraceDisabled" class="tw-mr-2">{{
            period.graceMonths ? "[" + period.graceTo + "]" : ""
          }}</span>
          <button @click="selectPeriod(period)">
            <i class="fa fa-edit tw-mx-1"></i>
          </button>
          <ConfirmPopover confirm-text="Delete" @confirmed="() => deletePeriod(period)">
            <template #trigger>
              <i class="fa fa-trash tw-mx-1"></i>
            </template>
            <template #question>Are you sure you want to delete this service period?</template>
          </ConfirmPopover>
          <span class="tw-text-xs tw-text-red-700">{{ validateOverlappingPeriod(period, period.id) }}</span>
        </div>
      </li>
    </ul>
  </div>
</template>
